// ======================================================
// CLASSEMENT DE MATURITÉ — SYSTÈME DE RÉFÉRENCE
// ======================================================

const RATING_SYSTEM_BY_LANGUAGE = {
    fr: "FR",
    en: "US",
    es: "ES",
    de: "DE",
};

const RATING_SYSTEM_BY_COUNTRY = {
    // Systèmes directement pris en charge.
    CA: "CA",
    US: "US",
    FR: "FR",
    ES: "ES",
    DE: "DE",

    // Famille française.
    MC: "FR",
    SN: "FR",
    CI: "FR",
    CM: "FR",

    // Famille allemande.
    AT: "DE",
    LI: "DE",

    // Famille espagnole.
    MX: "ES",
    AR: "ES",
    BO: "ES",
    CL: "ES",
    CO: "ES",
    CR: "ES",
    CU: "ES",
    DO: "ES",
    EC: "ES",
    GT: "ES",
    HN: "ES",
    NI: "ES",
    PA: "ES",
    PE: "ES",
    PY: "ES",
    SV: "ES",
    UY: "ES",
    VE: "ES",

    // Famille anglaise de référence.
    GB: "US",
    IE: "US",
    AU: "US",
    NZ: "US",
};

const MULTILINGUAL_RATING_COUNTRIES = new Set([
    "BE",
    "CH",
    "LU",
]);

const getRatingText = (t) => ({
    unrated: t("movie.rating.maturity.unrated"),
    allAges: t("movie.rating.maturity.allAges"),
    parentalGuidance: t(
        "movie.rating.maturity.parentalGuidance"
    ),
    adultsOnly: t("movie.rating.maturity.adultsOnly"),

    ageAndOver: (age) =>
        t("movie.rating.maturity.ageAndOver")
            .replace("{{age}}", age),

    underAgeRestricted: (age) =>
        t("movie.rating.maturity.underAgeRestricted")
            .replace("{{age}}", age),
});

const QUEBEC_ADVISORY_KEYS = {
    ENF: "movie.rating.quebecAdvisories.ENF",
    DEC: "movie.rating.quebecAdvisories.DEC",
    VUL: "movie.rating.quebecAdvisories.VUL",
    ERO: "movie.rating.quebecAdvisories.ERO",
    VIO: "movie.rating.quebecAdvisories.VIO",
    HOR: "movie.rating.quebecAdvisories.HOR",
    SEX: "movie.rating.quebecAdvisories.SEX",
};

export const getQuebecAdvisoryLabels = (
    advisories = [],
    t
) => {
    if (!Array.isArray(advisories)) {
        return [];
    }

    return [
        ...new Set(
            advisories
                .map((advisory) =>
                    String(advisory || "")
                        .trim()
                        .toUpperCase()
                )
                .filter(Boolean)
                .map((advisory) => {
                    const translationKey =
                        QUEBEC_ADVISORY_KEYS[advisory];

                    return translationKey
                        ? t(translationKey)
                        : advisory;
                })
        ),
    ];
};

export const resolveRatingSystem = ({
    country,
    state,
    language,
}) => {
    const normalizeLocationValue = (value) => {
        return String(value || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .trim()
            .toUpperCase();
    };

    const rawCountry =
        normalizeLocationValue(country);

    const normalizedCountry =
        rawCountry === "CANADA"
            ? "CA"
            : rawCountry;

    const normalizedState =
        normalizeLocationValue(state);

    const languageSystem =
        RATING_SYSTEM_BY_LANGUAGE[language] || "US";

    // Exception québécoise.
    if (
        normalizedCountry === "CA" &&
        ["QC", "QUEBEC"].includes(normalizedState)
    ) {
        return "QC";
    }

    // La langue choisie départage les pays multilingues.
    if (
        MULTILINGUAL_RATING_COUNTRIES.has(
            normalizedCountry
        )
    ) {
        return languageSystem;
    }

    return (
        RATING_SYSTEM_BY_COUNTRY[normalizedCountry] ||
        languageSystem
    );
};

const getRatingLookupCountry = (ratingSystem) => {
    // TMDB ne possède pas de territoire QC distinct.
    return ratingSystem === "QC"
        ? "CA"
        : ratingSystem;
};

const RELEASE_TYPE_PRIORITY = [
    3, // Sortie cinéma
    2, // Sortie cinéma limitée
    1, // Première
    4, // Sortie numérique
    5, // Sortie physique
    6, // Télévision
];

const findBestCertification = (
    releaseDates = []
) => {
    return [...releaseDates]
        .filter((release) => {
            return String(
                release?.certification || ""
            ).trim();
        })

        .sort((first, second) => {
            const firstIndex =
                RELEASE_TYPE_PRIORITY.indexOf(
                    first.type
                );

            const secondIndex =
                RELEASE_TYPE_PRIORITY.indexOf(
                    second.type
                );

            const firstRank =
                firstIndex === -1
                    ? RELEASE_TYPE_PRIORITY.length
                    : firstIndex;

            const secondRank =
                secondIndex === -1
                    ? RELEASE_TYPE_PRIORITY.length
                    : secondIndex;

            return firstRank - secondRank;
        })[0] || null;
};

export const normalizeCertificationCode = (
    certification = ""
) => {
    return String(certification)
        .trim()
        .toUpperCase()
        .replace(/\s+/g, " ");
};

export const formatCertificationLabel = ({
    system,
    code,
    t,
}) => {
    const text = getRatingText(t);
    const normalizedCode =
        normalizeCertificationCode(code);

    if (!normalizedCode) {
        return text.unrated;
    }

    // France
    if (system === "FR") {
        if (
            ["TP", "U", "TOUS PUBLICS"].includes(
                normalizedCode
            )
        ) {
            return text.allAges;
        }

        const age = normalizedCode.match(
            /(?:-|^)(12|16|18)(?:\+)?$/
        )?.[1];

        return age
            ? text.underAgeRestricted(age)
            : normalizedCode;
    }

    // Allemagne
    if (system === "DE") {
        const age = normalizedCode.match(
            /(?:FSK\s*)?(0|6|12|16|18)$/
        )?.[1];

        if (age === "0") {
            return text.allAges;
        }

        return age
            ? text.ageAndOver(age)
            : normalizedCode;
    }

    // Espagne
    if (system === "ES") {
        if (
            [
                "A",
                "APTA",
                "APTA PARA TODOS LOS PÚBLICOS",
            ].includes(normalizedCode)
        ) {
            return text.allAges;
        }

        if (normalizedCode === "X") {
            return text.adultsOnly;
        }

        const age = normalizedCode.match(
            /(?:^|[^0-9])(7|12|16|18)(?:\+)?(?:$|[^0-9])/
        )?.[1];

        return age
            ? text.underAgeRestricted(age)
            : normalizedCode;
    }

    // Canada et Québec
    if (
        system === "CA" ||
        system === "QC"
    ) {
        if (normalizedCode === "G") {
            return text.allAges;
        }

        if (normalizedCode === "PG") {
            return text.parentalGuidance;
        }

        const age = normalizedCode.match(
            /^(13|14|16|18)(?:A|\+)?$/
        )?.[1];

        if (age) {
            return text.ageAndOver(age);
        }

        if (
            ["R", "A"].includes(normalizedCode)
        ) {
            return text.adultsOnly;
        }

        return normalizedCode;
    }

    /*
     * Les certifications américaines G, PG,
     * PG-13, R et NC-17 sont déjà complètes.
     */
    return normalizedCode;
};

export const buildMaturityRating = ({
    movieAgeRatings,
    ratingSystem,
    language,
    t,
}) => {
    const preferredCountry =
        getRatingLookupCountry(ratingSystem);

    const languageCountry =
        RATING_SYSTEM_BY_LANGUAGE[language] ||
        "US";

    const fallbackCountries = [
        preferredCountry,
        languageCountry,
        "US",
    ].filter((country, index, countries) => {
        return (
            country &&
            countries.indexOf(country) === index
        );
    });

    for (const country of fallbackCountries) {
        const countryReleaseDates =
            movieAgeRatings.find((rating) => {
                return (
                    rating?.iso_3166_1?.toUpperCase() ===
                    country
                );
            });

        const bestRelease =
            findBestCertification(
                countryReleaseDates?.release_dates || []
            );

        if (!bestRelease) {
            continue;
        }

        const effectiveSystem =
            country === preferredCountry
                ? ratingSystem
                : country;

        const code =
            normalizeCertificationCode(
                bestRelease.certification
            );

        return {
            system: effectiveSystem,
            sourceCountry: country,
            code,

            label: formatCertificationLabel({
                system: effectiveSystem,
                code,
                t,
            }),

            // Point d’extension pour une future source fiable.
            advisories: [],
        };
    }

    return null;
};

export const getMaturityBadgePresentation = ({
    maturityRating,
    requestedSystem,
}) => {
    const system =
        maturityRating?.system ||
        requestedSystem ||
        "US";

    const code = normalizeCertificationCode(
        maturityRating?.code
    );

    const age =
        code.match(/\d{1,2}/)?.[0] ||
        null;

    let displayCode = code || "—";

    // France : TP, -12, -16, -18
    if (system === "FR") {
        if (
            ["TP", "U", "TOUS PUBLICS"].includes(code)
        ) {
            displayCode = "TP";
        } else if (age) {
            displayCode = `−${age}`;
        }
    }

    // Allemagne : l’indication FSK sera placée
    // dans l’en-tête de la pastille.
    if (system === "DE") {
        displayCode =
            age ||
            code.replace(/^FSK\s*/, "") ||
            "—";
    }

    // Espagne : A, +7, +12, +16, +18
    if (system === "ES") {
        if (
            [
                "A",
                "APTA",
                "APTA PARA TODOS LOS PÚBLICOS",
            ].includes(code)
        ) {
            displayCode = "A";
        } else if (age) {
            displayCode = `+${age}`;
        }
    }

    // Québec : G, 13+, 16+, 18+
    if (system === "QC" && age) {
        displayCode = `${age}+`;
    }

    let level = "guidance";

    const isAllAges =
        (system === "FR" &&
            ["TP", "U", "TOUS PUBLICS"].includes(code)) ||
        (system === "DE" && age === "0") ||
        (system === "ES" &&
            [
                "A",
                "APTA",
                "APTA PARA TODOS LOS PÚBLICOS",
            ].includes(code)) ||
        (
            ["QC", "CA", "US"].includes(system) &&
            code === "G"
        );

    const isParentalGuidance =
        code === "PG" ||
        (system === "DE" && age === "6") ||
        (
            system === "ES" &&
            age === "7"
        );

    const isAdultsOnly =
        (
            system === "US" &&
            ["R", "NC-17"].includes(code)
        ) ||
        (
            ["QC", "CA"].includes(system) &&
            ["R", "A"].includes(code)
        ) ||
        (
            system === "ES" &&
            code === "X"
        ) ||
        Number(age) >= 18;

    if (!code) {
        level = "unrated";
    } else if (isAllAges) {
        level = "all";
    } else if (isParentalGuidance) {
        level = "guidance";
    } else if (isAdultsOnly) {
        level = "adult";
    } else {
        level = "teen";
    }

    return {
        system:
            system === "DE"
                ? "FSK"
                : system,

        code: displayCode,
        level,
    };
};